import axios from "axios";
import { getApiRoute } from "src/services";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

/** Tiles a rail pulls per load-more, and rows the see-all A-Z list pulls. */
export const RAIL_PAGE_SIZE = 12;
export const SEE_ALL_PAGE_SIZE = 8;
export const LEADERBOARD_SIZE = 8;

const SECTION_CALENDARS_STALE_TIME_MS = 1000 * 60 * 60;

/**
 * Fetches one page of a single section's calendars.
 *
 * @param {{ slug: string, page: number, pageSize: number, sort: string, withEvents?: boolean }} params
 * @returns {Promise<{ calendars: Array<object>, page: number, total: number, hasMore: boolean }>}
 */
export async function getSectionCalendars({ slug, page, pageSize, sort, withEvents = true }) {
  const response = await axios.get(
    getApiRoute("home", "GET_SECTION_CALENDARS", { slug }),
    { params: { page, pageSize, sort, withEvents }, withCredentials: true },
  );
  return response.data;
}

export const sectionCalendarsQueryKey = (slug, sort, pageSize, initialPage, withEvents) => [
  "section-calendars",
  slug ?? null,
  sort,
  pageSize,
  initialPage,
  withEvents,
];

/**
 * Infinite list of one section's calendars from the stored snapshot. Backs both
 * a rail's horizontal load-more and the see-all page's leaderboard / A-Z lists.
 *
 * A rail sizes its pages to its seed plus a full page and starts at page 1, so
 * one fetch always yields a screen's worth however small the feed's slice was.
 * The overlap with the seed is dropped by the caller's merge.
 *
 * @param {string} slug
 * @param {{ sort?: "relevance"|"subscribers"|"name", pageSize?: number,
 *   initialPage?: number, enabled?: boolean, withEvents?: boolean }} params
 * @param {object} options - Passed through to react-query.
 */
export const useSectionCalendars = (
  slug,
  {
    sort = "relevance",
    pageSize = RAIL_PAGE_SIZE,
    initialPage = 1,
    enabled = true,
    withEvents = true,
  } = {},
  options = {},
) => {
  const query = useInfiniteQuery(
    sectionCalendarsQueryKey(slug, sort, pageSize, initialPage, withEvents),
    ({ pageParam = initialPage }) =>
      getSectionCalendars({ slug, page: pageParam, pageSize, sort, withEvents }),
    {
      enabled: Boolean(slug) && enabled,
      staleTime: SECTION_CALENDARS_STALE_TIME_MS,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) =>
        lastPage?.hasMore ? (lastPage.page ?? 0) + 1 : undefined,
      retry: (failureCount, error) =>
        error?.response?.status === 404 ? false : failureCount < 2,
      ...options,
    },
  );

  const calendars = useMemo(
    () => query.data?.pages.flatMap((page) => page.calendars ?? []) ?? [],
    [query.data],
  );

  return {
    ...query,
    calendars,
    total: query.data?.pages?.[0]?.total ?? 0,
  };
};
